// Despesas e abastecimentos: o que a empresa (ou o motorista, com o dinheiro
// do vale) gastou, com a foto do comprovante. O diesel pede os litros e o km
// do painel, confere a sequência do hodômetro e mostra o consumo. Compra a
// prazo vira parcelas, que aparecem em "A pagar". Tudo funciona sem sinal.
import {
  anexos, categorias, colaboradores, contasFixas, despesas, lembrarJeito, licencas, manutencao, parcelas, situacoesDeEnvio, ultimoJeito,
  veiculos, viagens,
} from '../dados.js';
import { conferirHodometro, consumoDoAbastecimento, dividirParcelas, somarDias, somarMeses } from '../lib/despesas.js';
import { h, icone, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import {
  data, dinheiro, lerInteiro, lerNumero, litros as formatarLitros, mesPorExtenso, mesCurto, numeroBR, placa as formatarPlaca,
} from '../lib/formato.js';
import { porVeiculo, situacaoDaPlaca } from '../lib/licencas.js';
import { botaoDeLerNota } from '../ui/nota-ia.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, emblema, faixa, formulario, secao, trocarOpcoes, vazio,
} from '../ui/componentes.js';
import { botoesDeFoto, miniaturaDaPrevia, miniaturaDoAnexo } from '../ui/fotos.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';
import { FORMAS_PAGAMENTO, NOME_FORMA, dialogoPagar, nomeDaDespesa } from './pagar.js';
import { blocoManutencaoFeita } from './manutencao.js';
import { nomeCurto, textoCaminhao } from './viagens.js';

const GRUPOS = [['viagem', 'Viagem'], ['manutencao', 'Manutenção'], ['fixo', 'Contas fixas'], ['imposto', 'Impostos'], ['outros', 'Outros']];

// Categorias em grupos, para o select. `primeiro`: o grupo que vem antes.
export function opcoesDeCategoria(cats, { primeiro = null } = {}) {
  const grupos = primeiro ? [...GRUPOS.filter(([g]) => g === primeiro), ...GRUPOS.filter(([g]) => g !== primeiro)] : GRUPOS;
  return grupos
    .map(([g, nome]) => ({ grupo: nome, opcoes: cats.filter((c) => c.grupo === g).map((c) => [c.id, c.nome]) }))
    .filter((g) => g.opcoes.length > 0);
}

export function textoCarreta(c, frota) {
  const trator = frota.find((t) => t.id === c.acoplado_a);
  return `${formatarPlaca(c.placa)}${c.apelido ? ` · ${c.apelido}` : ''}${trator ? ` (no ${formatarPlaca(trator.placa)})` : ''}`;
}

// Veículos para escolher: caminhões, carretas e "nenhum" (despesa geral).
export function opcoesDeVeiculo(frota, atualId = null) {
  const tratores = frota.filter((v) => v.tipo === 'trator' && (v.ativo || v.id === atualId));
  const carretas = frota.filter((v) => v.tipo === 'implemento' && (v.ativo || v.id === atualId));
  return [
    ['', 'Escolha o veículo'],
    { grupo: 'Caminhões', opcoes: tratores.map((t) => [t.id, textoCaminhao(t)]) },
    { grupo: 'Carretas', opcoes: carretas.map((c) => [c.id, textoCarreta(c, frota)]) },
    { grupo: 'Sem veículo', opcoes: [['geral', 'Nenhum (despesa geral da empresa)']] },
  ].filter((o) => Array.isArray(o) || o.opcoes.length > 0);
}

// Consumo de cada abastecimento (Map id → consumo), por caminhão.
export function consumos(leituras, cats) {
  const ids = new Set(cats.filter((c) => c.exige_hodometro).map((c) => c.id));
  const abastecimentos = leituras.filter((l) => ids.has(l.categoria_id) && l.litros > 0);
  const mapa = new Map();
  const porCaminhao = new Map();
  for (const a of abastecimentos) {
    if (!porCaminhao.has(a.veiculo_id)) porCaminhao.set(a.veiculo_id, []);
    porCaminhao.get(a.veiculo_id).push(a);
  }
  for (const lista of porCaminhao.values()) {
    for (const a of lista) {
      const c = consumoDoAbastecimento(lista, a);
      if (c) mapa.set(a.id, c);
    }
  }
  return mapa;
}

// Cartão de uma despesa (lista, viagem, veículo). `sufixo`: o que vai no
// endereço para o "voltar" da despesa trazer de volta a quem abriu.
export function cartaoDespesa(l, { cats, frota, pessoas, parc, envio, consumo = null, sufixo = '' }) {
  const cat = cats.find((c) => c.id === l.categoria_id);
  const veic = frota.find((v) => v.id === l.veiculo_id);
  const minhas = parc.filter((p) => p.lancamento_id === l.id);
  const pagas = minhas.filter((p) => p.pago_em).length;
  const quem = l.pago_por === 'colaborador' ? pessoas.find((p) => p.id === l.colaborador_id) : null;
  const detalhe = l.litros != null
    ? `${formatarLitros(l.litros)} L${l.hodometro != null ? ` · km ${numeroBR(l.hodometro)}` : ''}${consumo ? ` · ${numeroBR(consumo.kmPorLitro, { casas: 2 })} km/l` : ''}`
    : [l.fornecedor, !l.recorrencia_id && l.descricao].filter(Boolean).join(' · ') || null;
  let situacao = null;
  if (l.condicao === 'a_prazo') {
    if (minhas.length > 0 && pagas === minhas.length) situacao = emblema('Paga', 'ok');
    else situacao = emblema(minhas.length > 1 ? `${pagas} de ${minhas.length} parcelas pagas` : 'A pagar', 'alerta');
  }
  return cartaoLink({
    href: `#/despesas/${l.id}${sufixo}`,
    simbolo: cat?.exige_hodometro ? 'bomba' : 'recibo',
    titulo: `${dinheiro(l.valor)} · ${l.recorrencia_id && l.descricao ? l.descricao : cat?.nome ?? 'Despesa'}`,
    linhas: [
      `${data(l.data)}${veic ? ` · ${formatarPlaca(veic.placa)}` : ''}${l.litros != null && l.fornecedor ? ` · ${l.fornecedor}` : ''}`,
      detalhe,
      quem ? `Pago por ${nomeCurto(quem)} (entra no acerto)` : null,
    ],
    emblemas: [situacao, emblemaEnvio(envio?.get(l.id))],
  });
}

// ---------------------------------------------------------------------
// Lista, mês a mês
// ---------------------------------------------------------------------
export function telaDespesas({ busca }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  const hoje = hojeDaEmpresa();
  const mes = /^\d{4}-\d{2}$/.test(busca.get('mes') ?? '') ? busca.get('mes') : hoje.slice(0, 7);
  const filtro = busca.get('veiculo') ?? '';
  const endereco = (m, v) => `/despesas?mes=${m}${v ? `&veiculo=${v}` : ''}`;

  (async () => {
    const [todas, cats, frota, pessoas, parc, envio, leituras] = await Promise.all([
      despesas.listar(), categorias.todas(), veiculos.listar(), colaboradores.listar(), parcelas.listar(),
      situacoesDeEnvio('lancamentos'), despesas.leituras(),
    ]);
    const gravavel = podeGravar();
    const doFiltro = todas.filter((l) => !filtro || (filtro === 'geral' ? !l.veiculo_id : l.veiculo_id === filtro));
    const doMes = doFiltro.filter((l) => l.data.slice(0, 7) === mes);
    const porConsumo = consumos(leituras, cats);
    const extras = { cats, frota, pessoas, parc, envio };

    const soma = (lista) => lista.reduce((s, l) => s + Math.round(Number(l.valor) * 100), 0) / 100;
    const ids = new Set(cats.filter((c) => c.exige_hodometro).map((c) => c.id));
    const abastecimentos = doMes.filter((l) => ids.has(l.categoria_id));
    const litros = abastecimentos.reduce((s, l) => s + Number(l.litros || 0), 0);
    const doMotorista = doMes.filter((l) => l.pago_por === 'colaborador');

    const seletor = campo({
      rotulo: 'Veículo',
      nome: 'filtro_veiculo',
      tipo: 'select',
      valor: filtro,
      opcoes: [['', 'Todos os veículos'], ...opcoesDeVeiculo(frota, filtro).slice(1).map((o) => (Array.isArray(o) ? o : { ...o, opcoes: o.opcoes.map(([v, t]) => [v, v === 'geral' ? 'Sem veículo (despesas gerais)' : t]) }))],
      aoMudar: (e) => tela.ir(endereco(mes, e.target.value)),
    });
    const sufixo = filtro && filtro !== 'geral' ? `&veiculo=${filtro}` : '';

    trocar(corpo,
      gravavel && h('div', { class: 'acoes-lado' },
        h('a', { class: 'botao', href: `#/despesas/nova?tipo=diesel${sufixo}` }, '+ Diesel'),
        h('a', { class: 'botao secundario', href: `#/despesas/nova${sufixo ? `?${sufixo.slice(1)}` : ''}` }, '+ Despesa')),
      h('nav', { class: 'mes-navegacao', 'aria-label': 'Mês' },
        h('a', { href: `#${endereco(somarMeses(`${mes}-01`, -1).slice(0, 7), filtro)}`, 'aria-label': 'Mês anterior' }, icone('voltar')),
        h('strong', null, mesPorExtenso(mes)),
        h('a', { href: `#${endereco(somarMeses(`${mes}-01`, 1).slice(0, 7), filtro)}`, 'aria-label': 'Próximo mês' }, icone('seta'))),
      seletor,
      doMes.length > 0 && h('dl', { class: 'linha-dados resumo' },
        h('dt', null, 'Total do mês'), h('dd', null, dinheiro(soma(doMes))),
        abastecimentos.length > 0 && [h('dt', null, `Diesel (${formatarLitros(litros)} L)`), h('dd', null, dinheiro(soma(abastecimentos)))],
        doMotorista.length > 0 && [h('dt', null, 'Pagas pelo motorista'), h('dd', null, dinheiro(soma(doMotorista)))]),
      doMes.length > 0
        ? h('div', { class: 'lista' }, doMes.map((l) => cartaoDespesa(l, { ...extras, consumo: porConsumo.get(l.id) })))
        : vazio({
          simbolo: 'recibo',
          titulo: 'Nenhuma despesa neste mês',
          texto: todas.length === 0
            ? 'Lance o diesel, as manutenções e os outros gastos, com a foto do comprovante. Sem sinal também funciona.'
            : 'Use as setas para ver os outros meses.',
        }),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Despesas', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Nova despesa, ver e editar
// ---------------------------------------------------------------------
export function rotular(bloco, texto, obrigatorio = false) {
  const rotulo = bloco.querySelector('label, legend');
  if (!rotulo) return;
  rotulo.replaceChildren(texto, obrigatorio ? h('span', { class: 'obrigatorio', 'aria-hidden': 'true' }, ' *') : '');
}

export function telaDespesa({ params, busca }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? (busca.get('tipo') === 'diesel' ? 'Abastecimento' : 'Nova despesa') : 'Despesa');
  const pagina = casca({ titulo, voltar: '/despesas', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const despesa = nova ? null : await despesas.obter(params.id);
    if (!nova && !despesa) {
      trocar(corpo, vazio({ titulo: 'Despesa não encontrada', texto: 'Ela pode ter sido excluída.', acao: h('a', { class: 'botao', href: '#/despesas' }, 'Ver despesas') }));
      return;
    }
    const [cats, todasCats, frota, lics, listaViagens, pessoas, leituras, envio, itensDoPlano] = await Promise.all([
      categorias.listar(), categorias.todas(), veiculos.listar(), licencas.listar(), viagens.listar(), colaboradores.listar(),
      despesas.leituras(), situacoesDeEnvio('lancamentos'), manutencao.listar(),
    ]);
    const [minhasParcelas, meusAnexos, envioParcelas, envioAnexos, conta] = despesa
      ? await Promise.all([
        parcelas.doLancamento(despesa.id), anexos.doLancamento(despesa.id), situacoesDeEnvio('parcelas'), situacoesDeEnvio('anexos'),
        despesa.recorrencia_id ? contasFixas.obter(despesa.recorrencia_id) : null,
      ])
      : [[], [], new Map(), new Map(), null];

    const hoje = hojeDaEmpresa();
    const porV = porVeiculo(lics);
    const catPorId = new Map(todasCats.map((c) => [c.id, c]));
    const escolhiveis = despesa && !cats.some((c) => c.id === despesa.categoria_id) && catPorId.has(despesa.categoria_id)
      ? [...cats, catPorId.get(despesa.categoria_id)]
      : cats;
    const diesel = cats.find((c) => c.codigo === 'diesel') ?? cats.find((c) => c.exige_hodometro);
    const idsAbastecimento = new Set(todasCats.filter((c) => c.exige_hodometro).map((c) => c.id));
    const pedidaViagem = busca.get('viagem');
    const pedidoVeiculo = busca.get('veiculo');
    const viagemPedida = listaViagens.find((x) => x.id === (despesa?.viagem_id ?? pedidaViagem)) ?? null;
    // Volta para quem abriu: a tela pedida (A pagar, veículo, conta fixa…), a
    // viagem ou a lista (no mês da despesa).
    const voltaPedida = /^\/[\w\-/]*$/.test(busca.get('volta') ?? '') ? busca.get('volta') : null;
    const voltarPara = (dia) => {
      if (voltaPedida) return voltaPedida;
      if (pedidaViagem) return `/viagens/${pedidaViagem}`;
      return `/despesas?mes=${dia.slice(0, 7)}${pedidoVeiculo ? `&veiculo=${pedidoVeiculo}` : ''}`;
    };
    const voltar = voltarPara(despesa?.data ?? hoje);
    linkVoltar?.setAttribute('href', `#${voltar}`);

    const tratores = frota.filter((v) => v.tipo === 'trator' && (v.ativo || v.id === despesa?.veiculo_id));
    const pagas = minhasParcelas.filter((p) => p.pago_em);
    const planoTravado = pagas.length > 0;
    const gravavel = podeGravar() && !despesa?.acerto_id;
    // Despesa do histórico (MaxCargo Pro): a placa não precisava de licença.
    const importada = despesa?.origem === 'importacao';

    const licenciado = (veic, dia) => situacaoDaPlaca(porV[veic.tipo === 'implemento' ? veic.acoplado_a : veic.id] ?? [], dia).ativa;
    const nomeDaPessoa = (p) => (p.apelido ? `${p.nome} (${p.apelido})` : p.nome);
    const textoViagem = (x) => `${data(x.data_saida)}${x.data_chegada ? ` a ${data(x.data_chegada)}` : ' (em andamento)'} · ${nomeCurto(pessoas.find((p) => p.id === x.motorista_id))}`;
    const opcoesViagem = (veiculoId) => {
      const doCaminhao = listaViagens.filter((x) => x.veiculo_id === veiculoId && x.status !== 'cancelada');
      const lista = [...doCaminhao.filter((x) => x.status === 'aberta'), ...doCaminhao.filter((x) => x.status !== 'aberta').slice(0, 8)];
      const atual = despesa?.viagem_id ? listaViagens.find((x) => x.id === despesa.viagem_id) : null;
      if (atual && !lista.includes(atual)) lista.push(atual);
      return [['', 'Sem viagem'], ...lista.map((x) => [x.id, textoViagem(x)])];
    };
    const viagemDoDia = (veiculoId, dia) => listaViagens.find((x) => x.veiculo_id === veiculoId && x.status !== 'cancelada'
      && x.data_saida <= dia && (!x.data_chegada || x.data_chegada >= dia))?.id ?? '';

    // Valores iniciais
    const catInicial = despesa?.categoria_id ?? (busca.get('tipo') === 'diesel' ? diesel?.id : busca.get('categoria')) ?? '';
    const jeito = nova && catInicial ? await ultimoJeito(catInicial) : null;
    const dataInicial = despesa?.data ?? (viagemPedida && hoje < viagemPedida.data_saida ? viagemPedida.data_saida : hoje);
    const veiculoInicial = despesa
      ? despesa.veiculo_id ?? 'geral'
      : pedidoVeiculo ?? viagemPedida?.veiculo_id ?? (tratores.length === 1 ? tratores[0].id : '');
    const viagemInicial = despesa ? despesa.viagem_id ?? '' : viagemPedida?.id ?? viagemDoDia(veiculoInicial, dataInicial);
    const pagoPorInicial = despesa?.pago_por ?? jeito?.pago_por ?? (viagemInicial ? 'colaborador' : 'empresa');
    const motoristaDa = (viagemId) => listaViagens.find((x) => x.id === viagemId)?.motorista_id ?? '';
    const colaboradorInicial = despesa?.colaborador_id ?? motoristaDa(viagemInicial);
    const ativos = pessoas.filter((p) => p.ativo || p.id === despesa?.colaborador_id)
      .sort((a, b) => (a.funcao === 'motorista') === (b.funcao === 'motorista') ? 0 : a.funcao === 'motorista' ? -1 : 1);

    // Campos (os que aparecem ou somem conforme a categoria, o veículo e
    // quem pagou)
    const somenteLeituraPlano = planoTravado ? true : null;
    const campoCategoria = campo({
      rotulo: 'Categoria', nome: 'categoria_id', tipo: 'select', obrigatorio: true, valor: catInicial,
      opcoes: [['', 'Escolha a categoria'], ...opcoesDeCategoria(escolhiveis)],
      aoMudar: () => aoMudarCategoria(),
    });
    const campoVeiculo = campo({
      rotulo: 'Veículo', nome: 'veiculo_id', tipo: 'select', obrigatorio: true, valor: veiculoInicial,
      opcoes: opcoesDeVeiculo(frota, despesa?.veiculo_id), aoMudar: () => aoMudarVeiculo(),
    });
    const campoViagem = campo({
      rotulo: 'Viagem', nome: 'viagem_id', tipo: 'select', valor: viagemInicial, opcoes: opcoesViagem(veiculoInicial),
      dica: 'Despesa ligada à viagem entra no resultado dela.', aoMudar: () => aoMudarViagem(),
    });
    const campoLitros = campo({
      rotulo: 'Litros', nome: 'litros', formato: 'litros', valor: despesa?.litros != null ? formatarLitros(despesa.litros) : '',
      atributos: { inputmode: 'decimal' }, aoMudar: () => atualizarKm(),
    });
    const campoHodometro = campo({
      rotulo: 'Hodômetro (km no painel)', nome: 'hodometro', formato: 'km', valor: despesa?.hodometro != null ? numeroBR(despesa.hodometro) : '',
      atributos: { inputmode: 'numeric' }, aoMudar: () => atualizarKm(),
    });
    // Manutenção preventiva feita nesta despesa (o plano do caminhão volta a contar).
    const jaMarcados = despesa?.manutencao_itens ?? [];
    const feita = blocoManutencaoFeita({ marcados: jaMarcados, aoMudar: () => rotularKm() });
    const textoConsumo = h('p', { class: 'consumo', 'aria-live': 'polite', hidden: true });
    const textoKm = h('p', null);
    const blocoKm = h('div', { class: 'aviso-km', role: 'status', hidden: true },
      h('strong', null, 'Confira o hodômetro'),
      textoKm,
      campo({ rotulo: 'Confirmo: a leitura está certa', nome: 'hodometro_confirmado', tipo: 'caixa', valor: despesa?.hodometro_confirmado ?? false }));
    const campoPagoPor = campo({
      rotulo: 'Quem pagou', nome: 'pago_por', tipo: 'segmentos', valor: pagoPorInicial, desativado: planoTravado,
      opcoes: [['empresa', 'A empresa'], ['colaborador', 'O motorista', 'entra no acerto']],
      aoMudar: () => aoMudarPagoPor(),
    });
    const campoColaborador = campo({
      rotulo: 'Quem pagou (colaborador)', nome: 'colaborador_id', tipo: 'select', valor: colaboradorInicial,
      opcoes: [['', 'Escolha a pessoa'], ...ativos.map((p) => [p.id, nomeDaPessoa(p)])],
      dica: 'O valor volta para ele no acerto.',
    });
    const campoForma = campo({
      rotulo: 'Forma de pagamento', nome: 'forma', tipo: 'select', valor: despesa ? despesa.forma ?? '' : jeito?.forma ?? '',
      opcoes: [['', 'Não informar'], ...FORMAS_PAGAMENTO],
    });
    const campoCondicao = campo({
      rotulo: 'Condição', nome: 'condicao', tipo: 'segmentos', valor: despesa?.condicao ?? 'a_vista', desativado: planoTravado,
      opcoes: [['a_vista', 'À vista'], ['a_prazo', 'A prazo', 'em parcelas']],
      aoMudar: () => ajustar(),
      dica: planoTravado ? 'Há parcela paga: para mudar o valor ou as parcelas, desfaça o pagamento antes.' : null,
    });
    const blocoParcelas = h('div', { class: 'grade-2' },
      campo({
        rotulo: 'Parcelas', nome: 'parcelas_n', formato: 'dia', valor: String(minhasParcelas.length || 1),
        atributos: { inputmode: 'numeric', readonly: somenteLeituraPlano }, aoMudar: () => atualizarParcelas(),
      }),
      campo({
        rotulo: '1º vencimento', nome: 'primeiro_vencimento', tipo: 'date',
        valor: minhasParcelas[0]?.vencimento ?? somarDias(dataInicial, 30),
        atributos: { readonly: somenteLeituraPlano }, aoMudar: () => atualizarParcelas(),
      }));
    const previaParcelas = h('ul', { class: 'parcelas-previa', 'aria-live': 'polite' });
    const campoFornecedor = campo({
      rotulo: 'Fornecedor', nome: 'fornecedor', valor: despesa ? despesa.fornecedor ?? '' : jeito?.fornecedor ?? '',
      atributos: { autocomplete: 'off' },
    });
    const detalhes = h('details', { class: 'mais-detalhes', open: Boolean(despesa?.documento_numero || despesa?.descricao) },
      h('summary', null, 'Nota e observação'),
      h('div', { class: 'lista' },
        campo({ rotulo: 'Nº da nota ou do cupom', nome: 'documento_numero', valor: despesa?.documento_numero ?? '' }),
        campo({ rotulo: 'Observação', nome: 'descricao', tipo: 'textarea', valor: despesa?.descricao ?? '' })));

    // Fotos da despesa nova: ficam aqui até salvar.
    const fotosNovas = [];
    const gradeFotos = h('div', { class: 'fotos' });
    const desenharFotos = () => trocar(gradeFotos, fotosNovas.map((foto) => miniaturaDaPrevia(foto, {
      aoRemover: () => {
        fotosNovas.splice(fotosNovas.indexOf(foto), 1);
        desenharFotos();
        lerNota.atualizar();
      },
    })));
    // Leitura da nota com IA: preenche o que está em branco com o que leu na foto.
    const lerNota = botaoDeLerNota({
      fotos: () => fotosNovas,
      atuais: () => ({
        valor: valorDe('valor'), data: valorDe('data'), hoje: hojeDaEmpresa(), litros: valorDe('litros'),
        numero: valorDe('documento_numero'), fornecedor: valorDe('fornecedor'), categoria: valorDe('categoria_id'),
      }),
      categorias: () => cats,
      preencher: (p) => {
        // O que veio da nota conta como escolhido pela pessoa: o jeito de
        // pagar da última despesa da categoria não passa por cima.
        const por = (nome, valor) => {
          const el = form.querySelector(`[name="${nome}"]`);
          if (!el) return;
          el.value = valor;
          mexidos.add(nome);
        };
        if (p.valor) por('valor', numeroBR(p.valor, { casas: 2 }));
        if (p.data) por('data', p.data);
        if (p.numero) {
          por('documento_numero', p.numero);
          detalhes.open = true;
        }
        if (p.fornecedor) por('fornecedor', p.fornecedor);
        if (p.categoria) {
          por('categoria_id', p.categoria);
          aoMudarCategoria();
        }
        if (p.litros) por('litros', formatarLitros(p.litros));
        if (p.data) aoMudarData();
        ajustar();
        atualizarParcelas();
        atualizarKm();
      },
    });
    const blocoFotos = nova && h('fieldset', { class: 'campo' },
      h('legend', null, 'Foto do comprovante'),
      gradeFotos,
      botoesDeFoto((lista) => {
        fotosNovas.push(...lista);
        desenharFotos();
        lerNota.atualizar();
      }),
      lerNota.botao,
      h('p', { class: 'dica' }, 'Opcional. Fica guardada no celular e sobe quando houver sinal.'));

    const valorDe = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
    const marcado = (nome) => form.querySelector(`[name="${nome}"]:checked`)?.value ?? '';
    const categoriaAtual = () => catPorId.get(valorDe('categoria_id')) ?? null;
    const veiculoAtual = () => frota.find((v) => v.id === valorDe('veiculo_id')) ?? null;
    const mexidos = new Set();

    function ajustar() {
      const cat = categoriaAtual();
      const veic = veiculoAtual();
      const caminhao = veic?.tipo === 'trator';
      const abastecimento = Boolean(cat?.exige_hodometro);
      campoViagem.hidden = !caminhao;
      campoLitros.hidden = !(caminhao && (abastecimento || cat?.codigo === 'arla'));
      rotular(campoLitros, 'Litros', abastecimento);
      campoHodometro.hidden = !caminhao;
      feita.mostrar(
        caminhao ? itensDoPlano.filter((i) => i.veiculo_id === veic.id) : [],
        caminhao && (cat?.grupo === 'manutencao' || jaMarcados.length > 0));
      rotularKm();
      rotular(campoFornecedor, abastecimento ? 'Posto' : 'Fornecedor (oficina, loja…)');
      const pagoPor = marcado('pago_por');
      campoColaborador.hidden = pagoPor !== 'colaborador';
      campoCondicao.hidden = pagoPor !== 'empresa';
      const aPrazo = pagoPor === 'empresa' && marcado('condicao') === 'a_prazo';
      blocoParcelas.hidden = !aPrazo || planoTravado;
      previaParcelas.hidden = !aPrazo || planoTravado;
      if (nova) titulo.textContent = abastecimento ? 'Abastecimento' : 'Nova despesa';
      atualizarKm();
      atualizarParcelas();
    }

    function rotularKm() {
      const abastecimento = Boolean(categoriaAtual()?.exige_hodometro);
      const pede = abastecimento || feita.pedeKm();
      rotular(campoHodometro, abastecimento ? 'Hodômetro (km no painel)' : pede ? 'Km no painel' : 'Km no painel (opcional)', pede);
    }

    function atualizarKm() {
      const cat = categoriaAtual();
      const veic = veiculoAtual();
      const km = valorDe('hodometro') ? lerInteiro(valorDe('hodometro')) : null;
      const dia = valorDe('data');
      blocoKm.hidden = true;
      textoConsumo.hidden = true;
      if (veic?.tipo !== 'trator' || !dia) return;
      const doVeiculo = leituras.filter((l) => l.veiculo_id === veic.id);
      if (km !== null && km >= 0) {
        const alerta = conferirHodometro(doVeiculo, { id: despesa?.id ?? null, data: dia, hodometro: km });
        if (alerta) {
          textoKm.textContent = alerta.mensagem;
          blocoKm.hidden = false;
        }
      }
      if (!cat?.exige_hodometro) return;
      const anteriores = doVeiculo.filter((l) => idsAbastecimento.has(l.categoria_id) && l.litros > 0 && l.id !== despesa?.id);
      const litros = lerNumero(valorDe('litros'));
      const c = km !== null && litros > 0 ? consumoDoAbastecimento(anteriores, { data: dia, hodometro: km, litros }) : null;
      if (c) {
        textoConsumo.textContent = `${numeroBR(c.km)} km desde o abastecimento de ${data(c.desde)} · ${numeroBR(c.kmPorLitro, { casas: 2 })} km/l`;
        textoConsumo.hidden = false;
      } else if (anteriores.filter((l) => l.data <= dia).length === 0) {
        textoConsumo.textContent = 'Primeiro abastecimento deste caminhão no HSA Frota: o consumo aparece a partir do próximo.';
        textoConsumo.hidden = false;
      }
    }

    function atualizarParcelas() {
      if (previaParcelas.hidden) return;
      const valor = lerNumero(valorDe('valor'));
      const n = lerInteiro(valorDe('parcelas_n'));
      const primeiro = valorDe('primeiro_vencimento');
      if (!(valor > 0) || !(n >= 1 && n <= 48) || !primeiro) {
        trocar(previaParcelas);
        return;
      }
      trocar(previaParcelas, dividirParcelas(valor, n, primeiro).map((p) =>
        h('li', null, h('span', null, `${p.numero}ª · ${data(p.vencimento)}`), h('span', null, dinheiro(p.valor)))));
    }

    async function aoMudarCategoria() {
      const cat = categoriaAtual();
      if (nova && cat) {
        // O jeito de pagar da última despesa desta categoria (se a pessoa
        // ainda não escolheu outro).
        const ultimo = await ultimoJeito(cat.id);
        if (ultimo) {
          if (!mexidos.has('pago_por') && ultimo.pago_por) {
            const r = form.querySelector(`[name="pago_por"][value="${ultimo.pago_por}"]`);
            if (r) r.checked = true;
          }
          if (!mexidos.has('forma')) form.querySelector('[name="forma"]').value = ultimo.forma ?? '';
          if (!mexidos.has('fornecedor') && ultimo.fornecedor) form.querySelector('[name="fornecedor"]').value = ultimo.fornecedor;
        }
      }
      ajustar();
    }

    function aoMudarVeiculo() {
      const veic = veiculoAtual();
      if (veic?.tipo === 'trator') {
        const atual = valorDe('viagem_id');
        const daquele = listaViagens.find((x) => x.id === atual)?.veiculo_id === veic.id;
        trocarOpcoes(campoViagem.querySelector('select'), opcoesViagem(veic.id), daquele ? atual : viagemDoDia(veic.id, valorDe('data')));
        aoMudarViagem({ automatico: true });
      }
      ajustar();
    }

    function aoMudarViagem({ automatico = false } = {}) {
      if (!automatico) mexidos.add('viagem_id');
      const motorista = motoristaDa(valorDe('viagem_id'));
      if (motorista && !mexidos.has('colaborador_id')) form.querySelector('[name="colaborador_id"]').value = motorista;
    }

    function aoMudarPagoPor() {
      if (marcado('pago_por') === 'colaborador' && !valorDe('colaborador_id')) {
        const motorista = motoristaDa(valorDe('viagem_id'));
        if (motorista) form.querySelector('[name="colaborador_id"]').value = motorista;
      }
      ajustar();
    }

    function aoMudarData() {
      const dia = valorDe('data');
      const veic = veiculoAtual();
      if (nova && dia && veic?.tipo === 'trator' && !mexidos.has('viagem_id')) {
        form.querySelector('[name="viagem_id"]').value = viagemDoDia(veic.id, dia);
        aoMudarViagem({ automatico: true });
      }
      if (nova && dia && !mexidos.has('primeiro_vencimento')) form.querySelector('[name="primeiro_vencimento"]').value = somarDias(dia, 30);
      atualizarKm();
      atualizarParcelas();
    }

    // O que vai para o banco.
    const montar = (d) => {
      const cat = catPorId.get(d.categoria_id);
      const veic = frota.find((v) => v.id === d.veiculo_id) ?? null;
      const caminhao = veic?.tipo === 'trator';
      const pagoPor = d.pago_por || 'empresa';
      const km = caminhao && d.hodometro ? lerInteiro(d.hodometro) : null;
      return {
        categoria_id: d.categoria_id,
        veiculo_id: veic?.id ?? null,
        viagem_id: caminhao ? d.viagem_id || null : null,
        valor: planoTravado ? Number(despesa.valor) : lerNumero(d.valor),
        data: d.data,
        litros: caminhao && (cat?.exige_hodometro || cat?.codigo === 'arla') && d.litros ? lerNumero(d.litros) : null,
        hodometro: km,
        hodometro_confirmado: km !== null && !blocoKm.hidden && d.hodometro_confirmado === true,
        pago_por: pagoPor,
        colaborador_id: pagoPor === 'colaborador' ? d.colaborador_id : null,
        forma: d.forma || null,
        condicao: pagoPor === 'empresa' ? d.condicao || 'a_vista' : 'a_vista',
        fornecedor: d.fornecedor || null,
        documento_numero: d.documento_numero || null,
        descricao: d.descricao || null,
        manutencao_itens: caminhao ? feita.marcados() : [],
      };
    };

    const form = formulario({
      filhos: [
        campoCategoria,
        campoVeiculo,
        campoViagem,
        h('div', { class: 'grade-2' },
          campo({
            rotulo: 'Valor (R$)', nome: 'valor', obrigatorio: true, formato: 'dinheiro',
            valor: despesa ? numeroBR(despesa.valor, { casas: 2 }) : '',
            atributos: { inputmode: 'decimal', readonly: somenteLeituraPlano }, aoMudar: () => atualizarParcelas(),
          }),
          campo({ rotulo: 'Data', nome: 'data', tipo: 'date', obrigatorio: true, valor: dataInicial, aoMudar: () => aoMudarData() })),
        feita.bloco,
        h('div', { class: 'grade-2' }, campoLitros, campoHodometro),
        textoConsumo,
        blocoKm,
        campoPagoPor,
        campoColaborador,
        campoForma,
        campoCondicao,
        blocoParcelas,
        previaParcelas,
        campoFornecedor,
        detalhes,
        blocoFotos,
      ],
      textoBotao: nova ? 'Salvar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        const cat = catPorId.get(d.categoria_id);
        const veic = frota.find((v) => v.id === d.veiculo_id);
        if (!cat) erros.categoria_id = 'Escolha a categoria.';
        if (!d.veiculo_id) erros.veiculo_id = 'Escolha o veículo (ou "Nenhum", se for despesa geral da empresa).';
        else if (cat?.exige_hodometro && veic?.tipo !== 'trator') erros.veiculo_id = 'Escolha o caminhão do abastecimento.';
        else if (veic?.tipo === 'implemento' && cat && !cat.permite_implemento) erros.veiculo_id = `${cat.nome} não é lançado na carreta: escolha o caminhão.`;
        const valor = planoTravado ? Number(despesa.valor) : lerNumero(d.valor);
        if (!(valor > 0)) erros.valor = 'Informe o valor, como 250,00.';
        if (!d.data) erros.data = 'Informe a data.';
        else if (veic && !importada && !licenciado(veic, d.data)) {
          const trator = veic.tipo === 'implemento' ? frota.find((t) => t.id === veic.acoplado_a) : veic;
          erros.data = `A placa ${formatarPlaca(trator?.placa ?? veic.placa)} não estava ativa em ${data(d.data)}.`;
        }
        const viagem = veic?.tipo === 'trator' ? listaViagens.find((x) => x.id === d.viagem_id) : null;
        if (viagem && d.data && d.data < viagem.data_saida) erros.data = `A viagem saiu em ${data(viagem.data_saida)}: a despesa não pode ser antes.`;
        if (veic?.tipo === 'trator') {
          const comLitros = cat?.exige_hodometro || cat?.codigo === 'arla';
          if (comLitros && d.litros) {
            const litros = lerNumero(d.litros);
            if (!(litros > 0 && litros <= 3000)) erros.litros = 'Confira os litros (ex.: 380 ou 380,5).';
          } else if (cat?.exige_hodometro) {
            erros.litros = 'Informe os litros abastecidos.';
          }
          const km = d.hodometro ? lerInteiro(d.hodometro) : null;
          if (d.hodometro && !(km >= 0)) erros.hodometro = 'Só números, sem vírgula.';
          else if (km === null && cat?.exige_hodometro) erros.hodometro = 'Informe o km do painel (hodômetro).';
          else if (km === null && feita.pedeKm()) erros.hodometro = 'Informe o km do painel: a manutenção marcada conta pelo km.';
          else if (km !== null && !blocoKm.hidden && !d.hodometro_confirmado) {
            erros.hodometro = 'Confira a leitura. Se estiver certa, marque "Confirmo" logo abaixo.';
          }
        }
        if (d.pago_por === 'colaborador' && !d.colaborador_id) erros.colaborador_id = 'Escolha quem pagou.';
        if (d.pago_por === 'empresa' && d.condicao === 'a_prazo' && !planoTravado) {
          const n = lerInteiro(d.parcelas_n);
          if (!(n >= 1 && n <= 48)) erros.parcelas_n = 'De 1 a 48 parcelas.';
          if (!d.primeiro_vencimento) erros.primeiro_vencimento = 'Informe o 1º vencimento.';
          else if (d.data && d.primeiro_vencimento < d.data) erros.primeiro_vencimento = 'O 1º vencimento não pode ser antes da data da despesa.';
        }
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = montar(d);
        const cat = catPorId.get(dados.categoria_id);
        const n = lerInteiro(d.parcelas_n) || 1;
        try {
          if (nova) {
            const plano = dados.condicao === 'a_prazo' ? dividirParcelas(dados.valor, n, d.primeiro_vencimento) : [];
            await despesas.criar(dados, { parcelas: plano, fotos: fotosNovas });
            await lembrarJeito(dados.categoria_id, { pago_por: dados.pago_por, forma: dados.forma, fornecedor: dados.fornecedor });
            avisar(cat?.exige_hodometro ? 'Abastecimento salvo.' : 'Despesa salva.');
            tela.ir(voltarPara(dados.data), { substituir: true });
            return;
          }
          let plano = null;
          if (!planoTravado) {
            const mudou = dados.condicao !== despesa.condicao || (dados.condicao === 'a_prazo' && (
              dados.valor !== Number(despesa.valor) || n !== minhasParcelas.length || d.primeiro_vencimento !== minhasParcelas[0]?.vencimento));
            if (mudou) plano = dados.condicao === 'a_prazo' ? dividirParcelas(dados.valor, n, d.primeiro_vencimento) : [];
          }
          await despesas.atualizar(despesa.id, dados, despesa, { parcelas: plano });
          avisar('Alterações salvas.');
          tela.renderizar();
        } catch (erro) {
          // O servidor achou a leitura fora da sequência (com dados que este
          // aparelho ainda não tinha): mostra a confirmação.
          if (/^HODOMETRO_(FORA_DA_SEQUENCIA|SALTO)$/.test(erro.regra ?? '')) {
            textoKm.textContent = mensagemDeErro(erro);
            blocoKm.hidden = false;
          }
          throw erro;
        }
      },
      extras: !nova && gravavel && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({
            titulo: 'Excluir esta despesa?',
            texto: `Use só para despesa lançada por engano. Ela vai para a lixeira${minhasParcelas.length ? ', com as parcelas' : ''}${meusAnexos.length ? ' e as fotos' : ''}.`,
            sim: 'Excluir',
            perigo: true,
          });
          if (!ok) return;
          try {
            await despesas.excluir(despesa.id);
            avisar('Despesa excluída.');
            tela.ir(voltar, { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Excluir despesa lançada por engano'),
    });
    for (const nome of ['pago_por', 'forma', 'fornecedor', 'colaborador_id', 'primeiro_vencimento']) {
      for (const el of form.querySelectorAll(`[name="${nome}"]`)) el.addEventListener('change', () => mexidos.add(nome));
    }
    ajustar();

    if (nova) {
      trocar(corpo, form);
      return;
    }

    // Despesa já lançada: resumo, parcelas, comprovante e os dados.
    const cat = catPorId.get(despesa.categoria_id);
    const veic = frota.find((v) => v.id === despesa.veiculo_id);
    titulo.textContent = cat?.exige_hodometro ? 'Abastecimento' : 'Despesa';
    const quem = pessoas.find((p) => p.id === despesa.colaborador_id);
    const doVeiculo = leituras.filter((l) => l.veiculo_id === despesa.veiculo_id && idsAbastecimento.has(l.categoria_id) && l.litros > 0);
    const consumo = cat?.exige_hodometro ? consumoDoAbastecimento(doVeiculo, despesa) : null;

    const executar = async (acao, sucesso) => {
      try {
        await acao();
        avisar(sucesso);
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    };
    const pagar = async (p) => {
      const r = await dialogoPagar({ titulo: `Pagar a ${p.numero}ª parcela`, valor: p.valor, vencimento: p.vencimento, formaPadrao: despesa.forma === 'faturado' ? null : despesa.forma });
      if (r) executar(() => parcelas.pagar(p.id, r), 'Pagamento registrado.');
    };
    const desfazer = async (p) => {
      const ok = await confirmar({ titulo: 'Desfazer o pagamento?', texto: 'A parcela volta para "A pagar".', sim: 'Desfazer' });
      if (ok) executar(() => parcelas.reabrir(p.id), 'A parcela voltou para "A pagar".');
    };
    const removerFoto = async (a) => {
      const ok = await confirmar({ titulo: 'Tirar esta foto da despesa?', texto: 'Use quando a foto for de outra despesa. Ela vai para a lixeira.', sim: 'Tirar foto', perigo: true });
      if (ok) executar(() => anexos.remover(a.id), 'Foto retirada.');
    };

    const cabecalho = h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'valor-grande' }, dinheiro(despesa.valor)),
      h('span', { class: 'cartao-titulo' }, nomeDaDespesa(despesa, { categoria: cat, veiculo: veic })),
      h('span', { class: 'cartao-linha' }, `${data(despesa.data)}${despesa.fornecedor ? ` · ${despesa.fornecedor}` : ''}`),
      despesa.litros != null && h('span', { class: 'cartao-linha' },
        `${formatarLitros(despesa.litros)} L${despesa.hodometro != null ? ` · km ${numeroBR(despesa.hodometro)}` : ''}`),
      consumo && h('span', { class: 'cartao-linha' },
        `Consumo: ${numeroBR(consumo.kmPorLitro, { casas: 2 })} km/l (${numeroBR(consumo.km)} km desde ${data(consumo.desde)})`),
      jaMarcados.length > 0 && h('span', { class: 'cartao-linha' },
        `Manutenção feita: ${jaMarcados.map((id) => itensDoPlano.find((i) => i.id === id)?.nome ?? 'item tirado do plano').join(', ')}${despesa.hodometro != null && despesa.litros == null ? ` · km ${numeroBR(despesa.hodometro)}` : ''}`),
      h('span', { class: 'cartao-linha' }, despesa.pago_por === 'colaborador'
        ? `Pago por ${quem?.nome ?? 'colaborador'} · entra no acerto`
        : `Pago pela empresa${despesa.forma ? ` · ${NOME_FORMA[despesa.forma] ?? despesa.forma}` : ''}${despesa.condicao === 'a_prazo' ? ' · a prazo' : ''}`),
      h('span', { class: 'cartao-emblemas' },
        despesa.condicao === 'a_prazo' && (minhasParcelas.length > 0 && pagas.length === minhasParcelas.length ? emblema('Paga', 'ok') : emblema('A pagar', 'alerta')),
        meusAnexos.length === 0 && emblema(despesa.sem_nota_motivo ? `Sem nota: ${despesa.sem_nota_motivo}` : 'Sem foto'),
        emblemaEnvio(envio.get(despesa.id)))));

    const blocoDeParcelas = despesa.condicao === 'a_prazo' && secao('Parcelas', h('div', { class: 'lista' },
      minhasParcelas.map((p) => h('div', { class: 'cartao parcela' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${p.numero}ª de ${p.total} · ${dinheiro(p.valor)}`),
        h('span', { class: 'cartao-linha' }, p.pago_em
          ? `Paga em ${data(p.pago_em)}${p.forma ? ` · ${NOME_FORMA[p.forma] ?? p.forma}` : ''}`
          : `Vence em ${data(p.vencimento)}`),
        h('span', { class: 'cartao-emblemas' },
          p.pago_em ? emblema('Paga', 'ok') : p.vencimento < hoje ? emblema('Vencida', 'perigo') : emblema('A pagar', 'alerta'),
          emblemaEnvio(envioParcelas.get(p.id))),
        gravavel && h('div', { class: 'acoes-lado' }, p.pago_em
          ? h('button', { class: 'botao texto', type: 'button', onClick: () => desfazer(p) }, 'Desfazer pagamento')
          : h('button', { class: 'botao secundario', type: 'button', onClick: () => pagar(p) }, 'Paguei'))))),
      minhasParcelas.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma parcela. Salve a despesa de novo com o número de parcelas.')),
    { contador: `${pagas.length} de ${minhasParcelas.length} pagas` });

    const blocoComprovante = secao('Comprovante', h('div', { class: 'lista' },
      meusAnexos.length > 0
        ? h('div', { class: 'fotos' }, meusAnexos.map((a) => miniaturaDoAnexo(a, {
          esperando: Boolean(envioAnexos.get(a.id)),
          aoRemover: gravavel ? () => removerFoto(a) : null,
        })))
        : h('p', { class: 'pequeno' }, 'Nenhuma foto do comprovante.'),
      despesa.sem_nota_motivo && h('p', null, `Sem nota: ${despesa.sem_nota_motivo} (informado no app do motorista).`),
      gravavel && botoesDeFoto(async (lista) => {
        for (const foto of lista) await anexos.adicionar({ lancamento_id: despesa.id, dia: despesa.data }, foto);
        avisar(lista.length > 1 ? 'Fotos guardadas.' : 'Foto guardada.');
        tela.renderizar();
      })));

    trocar(corpo,
      despesa.acerto_id && faixa('Esta despesa já entrou num acerto fechado: não muda mais.', 'info'),
      despesa.origem === 'importacao' && faixa('Esta despesa veio do MaxCargo Pro (programas antigos).', 'info'),
      despesa.recorrencia_id && faixa(h('span', null,
        'Lançada pela conta fixa ',
        h('a', { href: `#/contas-fixas/${despesa.recorrencia_id}` }, conta?.descricao ?? 'da empresa'),
        despesa.competencia ? ` (${mesCurto(despesa.competencia)}).` : '.'), 'info'),
      cabecalho,
      blocoDeParcelas,
      blocoComprovante,
      secao('Dados da despesa', form),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return pagina;
}
